import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useResponsive } from '@app/hooks/useResponsive';
import { searchApi, SearchResultCategory } from '@app/api/search.api';
import { CategoryType } from '@app/constants/categoriesList';
import * as S from './HeaderSearch.styles';
import { SearchDropdown } from '../searchDropdown/SearchDropdown';

export interface CategoryComponents {
  category: CategoryType;
  components: { id: number; name: string; url: string; keywords: string[]; categories: CategoryType[] }[];
}

const DEBOUNCE_MS = 350;

export const HeaderSearch: React.FC = () => {
  const { mobileOnly, isTablet } = useResponsive();
  const { pathname } = useLocation();

  const [query, setQuery] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [results, setResults] = useState<CategoryComponents[] | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<CategoryType[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchResults = useCallback(
    async (q: string, cats: CategoryType[]) => {
      if (q.length < 2) {
        setResults(null);
        return;
      }
      const res = await searchApi(q, cats.length > 0 ? cats : undefined);
      if (res) {
        const mapped: CategoryComponents[] = res.results.map((cat: SearchResultCategory) => ({
          category: cat.category,
          components: cat.items.map((item) => ({
            id: item.id,
            name: item.label,
            url: item.url,
            keywords: [item.label.toLowerCase(), (item.sub ?? '').toLowerCase()],
            categories: [cat.category],
          })),
        }));
        setResults(mapped.length > 0 ? mapped : []);
      }
    },
    [],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchResults(query, selectedCategories);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, selectedCategories, fetchResults]);

  useEffect(() => {
    setModalOpen(false);
    setOverlayOpen(false);
    setQuery('');
    setResults(null);
  }, [pathname]);

  const searchProps = {
    query,
    setQuery,
    data: results,
    isOverlayOpen,
    setOverlayOpen,
    selectedCategories,
    setSelectedCategories,
  };

  return (
    <>
      {mobileOnly && (
        <>
          <BaseButton
            type={isModalOpen ? 'ghost' : 'text'}
            icon={<S.SearchIcon onClick={() => setModalOpen(true)} />}
          />
          <S.SearchModal
            open={isModalOpen}
            closable={false}
            footer={null}
            onCancel={() => setModalOpen(false)}
            destroyOnClose
          >
            <SearchDropdown {...searchProps} />
          </S.SearchModal>
        </>
      )}

      {isTablet && <SearchDropdown {...searchProps} />}
    </>
  );
};
